import { Button, Grid, Group, Paper, Select, TextInput } from "@mantine/core";
import dayjs from "dayjs";
import "jspdf-autotable";
import { useEffect, useMemo, useState } from "react";
import { FaCalendar } from "react-icons/fa";
import { ApiSearchUsers } from "../../../api/usuarios";

const mapUserOption = (u) => ({
  value: String(u.pre_usuario_id ?? u.value),
  label:
    u.label ||
    `${u.nombres || ""} ${u.apellidos || ""} (${u.code || u.usuario || ""})`.trim(),
  code: u.code,
  usuario: u.usuario,
});

export default function Form({
  state,
  setState,
  formData,
  setFormData,
  handleSubmit,
  resetForm,
}) {
  const [userOptions, setUserOptions] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  const selectedTicketUserOption = useMemo(() => {
    const ticket = state.selectedTicket;
    if (!ticket?.pre_usuarios) return null;
    const u = ticket.pre_usuarios;
    return mapUserOption({
      pre_usuario_id: ticket.pre_usuario_id || u.pre_usuario_id,
      nombres: u.nombres,
      apellidos: u.apellidos,
      code: u.code,
      usuario: u.usuario,
    });
  }, [state.selectedTicket]);

  useEffect(() => {
    if (selectedTicketUserOption) {
      setUserOptions((prev) => {
        if (prev.some((o) => String(o.value) === String(selectedTicketUserOption.value))) {
          return prev;
        }
        return [selectedTicketUserOption, ...prev];
      });
    }
  }, [selectedTicketUserOption]);

  const searchUsers = async (query) => {
    const term = (query || "").trim();
    if (term.length < 2) {
      setUserOptions((prev) => {
        if (
          selectedTicketUserOption &&
          !prev.some((o) => String(o.value) === String(selectedTicketUserOption.value))
        ) {
          return [selectedTicketUserOption];
        }
        return selectedTicketUserOption ? [selectedTicketUserOption] : prev;
      });
      return;
    }

    setSearchingUsers(true);
    try {
      const res = await ApiSearchUsers({ q: term, limit: 20, estado: true });
      if (res.sms === "ok") {
        const options = (res.data || []).map(mapUserOption);
        if (
          selectedTicketUserOption &&
          !options.some((o) => String(o.value) === String(selectedTicketUserOption.value))
        ) {
          options.unshift(selectedTicketUserOption);
        }
        setUserOptions(options);
      } else {
        setUserOptions(selectedTicketUserOption ? [selectedTicketUserOption] : []);
      }
    } finally {
      setSearchingUsers(false);
    }
  };

  return (
    <Paper withBorder p="md" mb="md">
      <form onSubmit={handleSubmit}>
        <Grid gutter="md">
          <Grid.Col span={6}>
            <Select
              label="Usuario"
              placeholder="Escriba código, cédula o nombre..."
              searchable
              clearable
              nothingFound={
                searchingUsers ? "Buscando..." : "Escriba al menos 2 caracteres"
              }
              data={userOptions}
              value={
                formData.pre_usuario_id
                  ? String(formData.pre_usuario_id)
                  : null
              }
              onSearchChange={searchUsers}
              onChange={(value) => {
                const opt = userOptions.find(
                  (o) => String(o.value) === String(value)
                );
                setFormData((prev) => ({
                  ...prev,
                  pre_usuario_id: value,
                  user_code: opt?.code || "",
                }));
              }}
              filter={() => true}
              required
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="Periodo"
              value={formData.pre_periodo_id}
              onChange={(value) => {
                setState((prev) => ({
                  ...prev,
                  comidas: state.periodos.find((p) => p.value === value)
                    ?.comidas,
                }));
                formData.pre_comida_id = "";
                setFormData((prev) => ({ ...prev, pre_periodo_id: value }));
              }}
              data={state.periodos}
              required
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Fecha"
              type="date"
              disabled={formData.pre_periodo_id === ""}
              min={dayjs(
                state.periodos.find((p) => p.value === formData.pre_periodo_id)
                  ?.minDate
              ).format("YYYY-MM-DD")}
              max={dayjs(
                state.periodos.find((p) => p.value === formData.pre_periodo_id)
                  ?.maxDate
              ).format("YYYY-MM-DD")}
              icon={<FaCalendar size={14} />}
              value={formData.create_at}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  create_at: e.target.value,
                }))
              }
              required
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="Comida"
              data={state.comidas}
              value={formData.pre_comida_id}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, pre_comida_id: value }))
              }
              required
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Group>
              <Button type="submit" variant="filled">
                {state.selectedTicket ? "Actualizar" : "Crear"}
              </Button>
              <Button variant="default" onClick={resetForm}>
                Cancelar
              </Button>
            </Group>
          </Grid.Col>
        </Grid>
      </form>
    </Paper>
  );
}
